import type { UserResource } from "@clerk/react/types";
import type { MatchAndClubCountryCode } from "@teamz/common";
import type { i18n } from "i18next";

import { isClerkAPIResponseError } from "@clerk/react/errors";
import { type ClassValue, clsx } from "clsx";
import Decimal from "decimal.js";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function hasClubDivisionDico(countryCode: MatchAndClubCountryCode): boolean {
  return ["be", "ch", "lu"].includes(countryCode);
}

export function toggleLanguage(i18n: i18n) {
  i18n.changeLanguage(i18n.language === "en-US" ? "fr-FR" : "en-US");
};

export function fileToURL(file: File): string {
  const url = URL.createObjectURL(file);
  return url;
}

type UploadClerkProfileImageResponseType = {
  status: "success";
  publicUrl: string;
} | {
  status: "error";
  errorMsg: string;
};

export async function uploadClerkProfileImage(imgFile: File, user: UserResource | undefined | null): Promise<UploadClerkProfileImageResponseType> {
  if (!user) {
    throw new Error("Unable to get clerk user");
  }

  try {
    const { publicUrl } = await user.setProfileImage({
      file: imgFile,
    });

    if (!publicUrl) {
      throw new Error("Failed to upload image");
    }

    return { status: "success", publicUrl };
  }
  catch (error) {
    if (isClerkAPIResponseError(error)) {
      const errorMsg = error.errors.map(e => e.longMessage).join("\n");
      return { status: "error", errorMsg };
    }
    throw new Error("Failed to upload image");
  }
}

export function formatDate(date: string, locale: string) {
  const newDate = new Date(date);
  const formatter = new Intl.DateTimeFormat(locale, {
    month: "long",
    day: "numeric",
  });

  return formatter.format(newDate);
}
export function formatYear(date: string, locale: string) {
  const newDate = new Date(date);
  const formatter = new Intl.DateTimeFormat(locale, {
    year: "numeric",
  });

  return formatter.format(newDate);
}

export function formatPriceFromCents(price: number): number {
  return new Decimal(price).div(100).toNumber();
}

export function formatPrice(price: number): string {
  return Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(formatPriceFromCents(price));
}

export function getActiveTab(plan: string | null) {
  if (!plan)
    return "matchs";

  switch (plan) {
    case "pack_equipe":
      return "matchs";
    case "pack_tournoi":
      return "tournaments";
    case "pack_equipe_tournoi":
      return "matchs";
    default:
      return "matchs";
  }
}
